import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Database } from "@/integrations/supabase/types";

export type ProductOrigin = Database["public"]["Enums"]["product_origin"];
export type ProductCategory = Database["public"]["Enums"]["product_category"];

export interface Product {
  id: string;
  name: string;
  price: number;
  origin: ProductOrigin;
  category: ProductCategory;
  description: string | null;
  image_url: string | null;
  created_at: string;
  updated_at: string;
}

export type CreateProductInput = Omit<Product, "id" | "created_at" | "updated_at">;
export type UpdateProductInput = Partial<CreateProductInput> & { id: string };

// Mapeo de valores de origen a labels legibles
export const PRODUCT_ORIGIN_LABELS: Record<ProductOrigin, string> = {
  ikea: "IKEA",
  home_depot: "Home Depot",
  walmart: "Walmart",
  soriana: "Soriana",
  la_comer: "La Comer",
  blancos_damec: "Blancos Damec",
  otros: "Otros",
};

// Alias for backward compatibility
export const ORIGIN_LABELS = PRODUCT_ORIGIN_LABELS;

// Mapeo de valores de categoría a labels legibles
export const PRODUCT_CATEGORY_LABELS: Record<ProductCategory, string> = {
  limpieza: "Limpieza",
  decoracion: "Decoración",
  mantenimiento: "Mantenimiento",
  blancos: "Blancos",
  mobiliario: "Mobiliario",
  cocina: "Cocina",
};

// Alias for backward compatibility
export const CATEGORY_LABELS = PRODUCT_CATEGORY_LABELS;

export const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Product[];
    },
  });
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (product: CreateProductInput) => {
      const { data, error } = await supabase
        .from("products")
        .insert(product)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: "Producto creado",
        description: "El producto se ha creado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al crear producto",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateProductInput) => {
      const { data, error } = await supabase
        .from("products")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: "Producto actualizado",
        description: "El producto se ha actualizado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al actualizar producto",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: "Producto eliminado",
        description: "El producto se ha eliminado exitosamente",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error al eliminar producto",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
